import React, { useState, useEffect } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import Logo from '../assets/Fuat (1).svg';

const Navbar = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative w-full z-50">
      <div className={`container-nav relative transition-all duration-500 ${isScrolled ? 'py-2' : 'py-4'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative backdrop-blur-xl bg-black/20 border border-[#FF3366]/60 rounded-full shadow-lg shadow-[#FF3366]/5"
          >
            <div className="relative px-4 lg:px-6 py-1.5">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FF3366]/5 via-transparent to-[#FF6B4D]/5 rounded-full pointer-events-none" />

              <div className="flex items-center justify-between">
                <Link to="/" className="relative group flex items-center gap-0.5">
                  <motion.div
                    className="relative"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.7, ease: "easeInOut" }}
                  >
                    <div className="absolute -inset-2 bg-gradient-to-r from-primary/15 to-secondary/15 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-all duration-700" />
                    <div className="relative w-16 h-16 lg:w-20 lg:h-20 flex items-center justify-center">
                      <img src={Logo} alt="UIQuarter Logo" className="w-16 h-16 lg:w-20 lg:h-20" />
                    </div>
                  </motion.div>
                  <motion.span
                    className="text-lg lg:text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    UIQuarter
                  </motion.span>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden lg:flex items-center gap-8">
                  <motion.div
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <NavLink to="/" className={({ isActive }) => `nav-item text-lg ${isActive && location.pathname === '/' ? 'active' : ''}`}>
                      Home
                    </NavLink>
                    <NavLink to="/docs" className={({ isActive }) => `nav-item text-lg ${isActive ? 'active' : ''}`}>
                      Docs
                    </NavLink>
                    <NavLink to="/about" className={({ isActive }) => `nav-item text-lg ${isActive ? 'active' : ''}`}>
                      About
                    </NavLink>
                  </motion.div>

                  <div className="h-6 w-[1px] bg-gradient-to-b from-transparent via-white/10 to-transparent" />

                  <a
                    href="https://github.com/fuatkeles/uiquarter"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white font-semibold hover:opacity-90 transition-all duration-300 text-sm"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                    GitHub
                  </a>
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {isMobileMenuOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mobile Menu */}
              {isMobileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="lg:hidden mt-4 p-4 bg-black/30 backdrop-blur-xl rounded-lg border border-white/10"
                >
                  <div className="flex flex-col gap-4">
                    <NavLink to="/" className={({ isActive }) => `nav-item text-lg ${isActive && location.pathname === '/' ? 'active' : ''} block py-2`} onClick={() => setIsMobileMenuOpen(false)}>
                      Home
                    </NavLink>
                    <NavLink to="/docs" className={({ isActive }) => `nav-item text-lg ${isActive ? 'active' : ''} block py-2`} onClick={() => setIsMobileMenuOpen(false)}>
                      Docs
                    </NavLink>
                    <NavLink to="/about" className={({ isActive }) => `nav-item text-lg ${isActive ? 'active' : ''} block py-2`} onClick={() => setIsMobileMenuOpen(false)}>
                      About
                    </NavLink>
                    <div className="h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent my-2" />
                    <a
                      href="https://github.com/fuatkeles/uiquarter"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white font-semibold transition-all duration-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                      GitHub
                    </a>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.nav>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
